import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { CrossActionContext, TransactionViewContext } from 'features/transaction-details/types/transactionCrossContext';
import { copyToClipboard } from 'features/transaction-details/helpers';
import { ETicketDetailDialog } from '../ETicketDetailDialog';

interface ETicketServiceLinkProps {
	eticket: string;
	transactionContext?: TransactionViewContext;
	crossContext?: CrossActionContext;
	compact?: boolean;
	onCopySuccess: (message: string) => void;
	onRequestUnbind?: () => void;
	onNavigationError?: (message: string) => void;
	/** Enfoca el botón copiar al montar (deep link ?highlightEticket=). */
	autoFocusCopy?: boolean;
}

export const ETicketServiceLink = ({
	eticket,
	transactionContext,
	crossContext,
	compact = false,
	onCopySuccess,
	onRequestUnbind,
	onNavigationError,
	autoFocusCopy = false,
}: ETicketServiceLinkProps): JSX.Element => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const copyButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!autoFocusCopy) return;
		const timer = window.setTimeout(() => {
			copyButtonRef.current?.focus();
		}, 450);
		return () => window.clearTimeout(timer);
	}, [autoFocusCopy]);

	const handleCopy = async (e: React.MouseEvent): Promise<void> => {
		e.stopPropagation();
		const success = await copyToClipboard(eticket);
		if (success) onCopySuccess('E-Ticket copiado');
	};

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.25 : 1 }}>
				<Tooltip title="Ver detalles del boleto y venta asociada">
					{compact ? (
						<Button
							size="small"
							variant="text"
							onClick={() => setDialogOpen(true)}
							sx={{
								textTransform: 'none',
								fontFamily: 'monospace',
								fontWeight: 600,
								fontSize: '0.8125rem',
								minWidth: 0,
								px: 0.5,
								py: 0,
							}}
						>
							{eticket}
						</Button>
					) : (
						<Button
							size="small"
							variant="outlined"
							startIcon={<ConfirmationNumberIcon />}
							onClick={() => setDialogOpen(true)}
							sx={{ textTransform: 'none' }}
						>
							{eticket}
						</Button>
					)}
				</Tooltip>
				<Tooltip title="Copiar E-Ticket">
					<IconButton ref={copyButtonRef} size="small" onClick={handleCopy} aria-label={`Copiar e-ticket ${eticket}`}>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			<ETicketDetailDialog
				open={dialogOpen}
				eticket={eticket}
				transactionContext={transactionContext}
				crossContext={crossContext}
				onClose={() => setDialogOpen(false)}
				onCopySuccess={onCopySuccess}
				onRequestUnbind={
					onRequestUnbind
						? () => {
								setDialogOpen(false);
								onRequestUnbind();
						  }
						: undefined
				}
				onNavigationError={onNavigationError}
			/>
		</>
	);
};
